import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { BHAVA_NAMES, GRAHA_NAMES, RASHI_NAMES, type GrahaBody } from '@astrally/shared';
import { useI18n } from '../../i18n';
import { Term } from '../../components/Term';
import { isTopOverlay, popOverlay, pushOverlay } from '../../components/overlayStack';
import { findTerm } from '../glossary/terms';

/* --- Tappable kundli compartments ------------------------------------------

   The kundli image itself is the interface: every bhava compartment in the
   North / South / East chart styles carries a transparent SVG hit area, and a
   tap opens a small anchored popover naming the bhava, the rashi occupying it
   and each graha sitting there — with the names themselves <Term>-tappable,
   so the reader can fall from "what is in my 7th" straight through to "what
   is a bhava at all" without leaving the chart.

   The popover deliberately wears the SAME clothes as the glossary popover
   (`.termpop` in global.css) and repeats its positioning contract: portal to
   document.body, prefer below, flip above when the viewport under the anchor
   is tight, clamp to 8px margins, close on scroll/outside tap/Escape.
   Term.tsx keeps its measuring logic private (it wraps a whole entry, not
   arbitrary children), so the small measure/dismiss block is mirrored here
   rather than reaching into a file another change owns.

   data-mode is read from the tapped compartment's nearest [data-mode]
   ancestor and stamped on the popover root — tradition palettes live on
   subtrees and do not survive a body portal. [data-theme] lives on <html>,
   which is still an ancestor, so light/dark needs nothing.
*/

export interface HouseTapInfo {
  /** 1-12 counted from the lagna; null when the chart has no lagna to count from. */
  bhava: number | null;
  /** 0-11 rashi index (0 = Mesha). */
  sign: number;
  /** The grahas sitting in this compartment. */
  grahas: GrahaBody[];
}

/** Glossary ids per rashi index, for <Term> wraps and popup lookups. */
export const RASHI_TERM_IDS = [
  'rashi.mesha',
  'rashi.vrishabha',
  'rashi.mithuna',
  'rashi.karka',
  'rashi.simha',
  'rashi.kanya',
  'rashi.tula',
  'rashi.vrishchika',
  'rashi.dhanu',
  'rashi.makara',
  'rashi.kumbha',
  'rashi.meena',
] as const;

/** Glossary ids per graha body, same purpose. */
export const GRAHA_TERM_IDS: Record<GrahaBody, string> = {
  sun: 'graha.surya',
  moon: 'graha.chandra',
  mars: 'graha.mangal',
  mercury: 'graha.budh',
  jupiter: 'graha.guru',
  venus: 'graha.shukra',
  saturn: 'graha.shani',
  rahu: 'graha.rahu',
  ketu: 'graha.ketu',
};

/** 1 → "1st" … 12 → "12th", for spoken-order aria labels. */
function ordinal(n: number): string {
  if (n === 1) return '1st';
  if (n === 2) return '2nd';
  if (n === 3) return '3rd';
  return `${n}th`;
}

/**
 * The accessible name of a compartment: "4th bhava — Karka rashi, Chandra".
 * Charts without a lagna (South/East in window modes) cannot number bhavas,
 * so the label honestly starts at the rashi instead of guessing a count.
 */
export function houseHitLabel(info: HouseTapInfo): string {
  const rashi = `${RASHI_NAMES[info.sign]} rashi`;
  const head = info.bhava === null ? rashi : `${ordinal(info.bhava)} bhava — ${rashi}`;
  const grahas = info.grahas.map((g) => GRAHA_NAMES[g]).join(', ');
  return grahas ? `${head}, ${grahas}` : head;
}

export type HitShape =
  | { kind: 'polygon'; points: string }
  | { kind: 'rect'; x: number; y: number; width: number; height: number };

/** Where the invitation dot sits: the vertex average, close enough for these shapes. */
export function shapeCentre(shape: HitShape): [number, number] {
  if (shape.kind === 'rect') return [shape.x + shape.width / 2, shape.y + shape.height / 2];
  const pts = shape.points
    .trim()
    .split(/\s+/)
    .map((pair) => pair.split(',').map(Number));
  let sx = 0;
  let sy = 0;
  for (const [x, y] of pts) {
    sx += x;
    sy += y;
  }
  return [sx / pts.length, sy / pts.length];
}

export interface KundliTouchState {
  explain: boolean;
  toggleExplain: () => void;
  active: { info: HouseTapInfo; anchor: SVGGElement } | null;
  tap: (info: HouseTapInfo, anchor: SVGGElement) => void;
  close: () => void;
}

/** One chart card's worth of tap state: the explain toggle plus one open popover. */
export function useKundliTouch(): KundliTouchState {
  const [explain, setExplain] = useState(false);
  const [active, setActive] = useState<KundliTouchState['active']>(null);
  const toggleExplain = useCallback(() => setExplain((v) => !v), []);
  // Tapping the compartment that is already open closes it. The popover's
  // outside-pointerdown handler ignores the active anchor precisely so this
  // toggle is what runs, instead of close-then-instantly-reopen.
  const tap = useCallback((info: HouseTapInfo, anchor: SVGGElement) => {
    setActive((cur) => (cur && cur.anchor === anchor ? null : { info, anchor }));
  }, []);
  const close = useCallback(() => setActive(null), []);
  return { explain, toggleExplain, active, tap, close };
}

export function HouseHitArea({
  shape,
  info,
  explain,
  active,
  onTap,
}: {
  shape: HitShape;
  info: HouseTapInfo;
  explain: boolean;
  active: boolean;
  onTap: (info: HouseTapInfo, anchor: SVGGElement) => void;
}) {
  const [cx, cy] = shapeCentre(shape);
  return (
    <g
      role="button"
      tabIndex={0}
      aria-label={houseHitLabel(info)}
      aria-expanded={active}
      style={{ cursor: 'pointer' }}
      onClick={(e) => {
        // Chart cards can sit inside clickable surfaces.
        e.stopPropagation();
        onTap(info, e.currentTarget);
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        e.stopPropagation();
        onTap(info, e.currentTarget);
      }}
    >
      {shape.kind === 'polygon' ? (
        <polygon points={shape.points} fill="transparent" />
      ) : (
        <rect x={shape.x} y={shape.y} width={shape.width} height={shape.height} fill="transparent" />
      )}
      {/* The pulsing touch-dot the "?" toggle turns on: SVG <animate>, so no
          new keyframes are smuggled into a stylesheet this change does not
          own. --gold is a FILL here, which is the allowed use of that token. */}
      {explain && (
        <circle cx={cx} cy={cy} r={4.5} fill="var(--gold)" pointerEvents="none">
          <animate attributeName="r" values="3.2;6.2;3.2" dur="1.8s" repeatCount="indefinite" />
          <animate attributeName="opacity" values="0.9;0.35;0.9" dur="1.8s" repeatCount="indefinite" />
        </circle>
      )}
    </g>
  );
}

/** The "?" chip on a chart card header — toggles the invitation dots. */
export function ExplainChip({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  const { t } = useI18n();
  return (
    <button
      type="button"
      className={`chip${on ? ' chip--fact' : ''}`}
      aria-pressed={on}
      aria-label={t('kundli.explainToggle')}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      ?
    </button>
  );
}

interface PopPosition {
  top: number;
  left: number;
  /** Caret centre, px from the popover's left edge. */
  caretX: number;
  above: boolean;
}

/** Flip above the compartment when less than this much viewport remains below. */
const FLIP_AT = 200;
/** Gap between the compartment and the popover, spanned by the caret. */
const GAP = 10;
/** Minimum distance from the viewport edges. */
const MARGIN = 8;

export function KundliPopover({
  info,
  anchor,
  onClose,
}: {
  info: HouseTapInfo;
  anchor: SVGGElement;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const popRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PopPosition | null>(null);
  // The anchor cannot change traditions while the popover is open, so reading
  // its palette during render is safe.
  const mode = anchor.closest('[data-mode]')?.getAttribute('data-mode') ?? undefined;

  useLayoutEffect(() => {
    const measure = () => {
      const pop = popRef.current;
      if (!pop) return;
      const r = anchor.getBoundingClientRect();
      const pw = pop.offsetWidth;
      const ph = pop.offsetHeight;
      const centerX = r.left + r.width / 2;
      const maxLeft = Math.max(window.innerWidth - MARGIN - pw, MARGIN);
      const left = Math.min(Math.max(centerX - pw / 2, MARGIN), maxLeft);
      const above = window.innerHeight - r.bottom < FLIP_AT;
      setPos({
        top: above ? r.top - ph - GAP : r.bottom + GAP,
        left,
        caretX: Math.min(Math.max(centerX - left, 14), Math.max(pw - 14, 14)),
        above,
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [anchor]);

  useEffect(() => {
    const onPointerDown = (e: Event) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (popRef.current?.contains(target) || anchor.contains(target)) return;
      // A glossary popover opened from inside this popup is part of the same
      // reading, not an "outside" tap.
      if (target.closest('.termpop')) return;
      onClose();
    };
    // A glossary popover can be open ON TOP of this one — tapping "Chandra"
    // inside the explanation is the whole point. Escape must dismiss only the
    // topmost layer; see components/overlayStack.ts for why registration
    // order cannot be relied on to do that.
    const token = pushOverlay('kundli-popover');
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!isTopOverlay(token)) return;
      onClose();
      anchor.focus();
    };
    const onScroll = () => onClose();
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      popOverlay(token);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [anchor, onClose]);

  const bhava = info.bhava === null ? null : BHAVA_NAMES[info.bhava - 1];

  return createPortal(
    <div
      ref={popRef}
      role="dialog"
      aria-label={houseHitLabel(info)}
      className={`termpop ${pos?.above ? 'termpop--above' : 'termpop--below'}`}
      data-mode={mode}
      style={{
        top: pos?.top ?? 0,
        left: pos?.left ?? 0,
        // Painted only once the first measure has run.
        visibility: pos ? undefined : 'hidden',
      }}
    >
      <span className="termpop__caret" aria-hidden style={{ left: pos?.caretX ?? 14 }} />
      {bhava && (
        <>
          <div className="h3">
            <Term id={`bhava.${bhava.n}`}>
              {bhava.n} bhava · {bhava.name}
            </Term>
          </div>
          <div className="tiny muted" style={{ marginTop: 2, lineHeight: 1.5 }}>
            {bhava.gist}
          </div>
        </>
      )}
      <div className={bhava ? 'small' : 'h3'} style={{ marginTop: bhava ? 8 : 0 }}>
        <Term id={RASHI_TERM_IDS[info.sign]}>{RASHI_NAMES[info.sign]}</Term>{' '}
        <span className="tiny faint">· {t('kundli.pop.rashiNum', { n: info.sign + 1 })}</span>
      </div>
      {info.grahas.length > 0 ? (
        <div className="col" style={{ gap: 6, marginTop: 8 }}>
          {info.grahas.map((g) => (
            <div key={g} className="tiny" style={{ lineHeight: 1.5 }}>
              <Term id={GRAHA_TERM_IDS[g]}>
                <b>{GRAHA_NAMES[g]}</b>
              </Term>
              <span className="muted"> — {findTerm(GRAHA_TERM_IDS[g])?.short}</span>
            </div>
          ))}
        </div>
      ) : (
        <div className="tiny muted" style={{ marginTop: 8, lineHeight: 1.5 }}>
          {t('kundli.pop.empty')}
        </div>
      )}
    </div>,
    document.body,
  );
}
